import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Camera, ChevronRight, ImageOff, ImagePlus, LoaderCircle, LocateFixed, MapPin, Plus, RotateCcw, Trash2, X } from 'lucide-react';
import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface Segel {
    id: string;
    nsb: string;
    nama: string;
    alamat: string;
    jumlah: string;
    foto_rumah?: string; // opsional: dipakai kalau API list sudah mengirim foto terbaru
}

interface Pelanggan {
    nsb: string;
    nama: string;
    alamat?: string;
    stat_smb?: string;
}

interface SegelHistory {
    id?: string;
    nama?: string;
    nsb?: string;
    alamat?: string;
    m_lalu?: string;
    status?: string;
    foto_meter?: any;
    foto_rumah?: string;
    koordinat?: string;
    modified_at?: string;
}

interface GeoPoint {
    lat: number;
    lng: number;
}

type DeleteTarget = { kind: 'segel' } | { kind: 'history'; item: SegelHistory };
type SegelModal = 'detail' | 'form' | 'map' | 'camera' | 'delete' | 'preview';

function getSegelModalStack(state: unknown): SegelModal[] {
    if (!state || typeof state !== 'object' || !('segelModalStack' in state)) return [];

    const stack = state.segelModalStack;
    if (!Array.isArray(stack)) return [];

    return stack.filter(
        (modal): modal is SegelModal =>
            ['detail', 'form', 'map', 'camera', 'delete', 'preview'].includes(modal),
    );
}

interface ApiResponse<T> {
    status?: number;
    message?: string;
    data?: T;
}

const API_URL = process.env.REACT_APP_API_URL_V2 ?? '';
const API_KEY = process.env.REACT_APP_API_KEY ?? '';
const STORAGE_URL = process.env.REACT_APP_API_URL_STORAGE ?? '';

const fotoUrl = (path: string) => STORAGE_URL + path;

// Endpoint hapus (sesuaikan dengan backend)
const deleteSegelUrl = (id: string) => `${API_URL}/hublang/segel/${encodeURIComponent(id)}`;
const deleteHistoryUrl = (id: string) => `${API_URL}/hublang/segel/history/${encodeURIComponent(id)}`;

// Titik tengah peta kalau belum ada lokasi dan GPS belum tersedia (ubah sesuai wilayah layanan)
const DEFAULT_MAP_CENTER: GeoPoint = { lat: -7.9135, lng: 113.8214 };

// Pin peta berupa SVG, jadi tidak butuh file gambar marker bawaan Leaflet
const PIN_ICON = L.divIcon({
    className: 'segel-pin',
    html: '<svg width="32" height="40" viewBox="0 0 24 30" xmlns="http://www.w3.org/2000/svg"><path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 18 12 18s12-9 12-18C24 5.4 18.6 0 12 0z" fill="#2563eb" stroke="#fff" stroke-width="1.5"/><circle cx="12" cy="12" r="4.5" fill="#fff"/></svg>',
    iconSize: [32, 40],
    iconAnchor: [16, 40],
});

// "lat,lng" -> { lat, lng }
function parseKoordinat(value?: string): GeoPoint | null {
    if (!value) return null;

    const [lat, lng] = value.split(',').map((part) => parseFloat(part.trim()));
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
}

// Koordinat terbaru dari daftar riwayat (dipakai sebagai isian awal saat tambah history)
function latestKoordinat(rows: SegelHistory[]): GeoPoint | null {
    const sorted = [...rows].sort(
        (a, b) => new Date(b.modified_at ?? 0).getTime() - new Date(a.modified_at ?? 0).getTime(),
    );

    for (const row of sorted) {
        const point = parseKoordinat(row.koordinat);
        if (point) return point;
    }
    return null;
}

function parseFotoMeter(value: any) {
    if (!value) return {};
    if (typeof value !== 'string') return value;

    try {
        return JSON.parse(value);
    } catch {
        return {};
    }
}

async function fetchHistoryRows(nsb: string): Promise<SegelHistory[]> {
    const response = await fetch(
        `${API_URL}/hublang/segel/history/${encodeURIComponent(nsb)}`,
        { headers: { Authorization: API_KEY } },
    );
    const result = await response.json();
    const rows = Array.isArray(result) ? result : result?.data;

    return Array.isArray(rows)
        ? rows.map((item: SegelHistory) => ({
              ...item,
              foto_meter: parseFotoMeter(item.foto_meter),
          }))
        : [];
}

function getLatestRow(rows: SegelHistory[]): SegelHistory | null {
    if (rows.length === 0) return null;

    return rows.reduce((a, b) =>
        new Date(b.modified_at ?? 0).getTime() > new Date(a.modified_at ?? 0).getTime() ? b : a,
    );
}

function getLatestHousePhoto(rows: SegelHistory[]): string {
    return getLatestRow(rows.filter((row) => row.foto_rumah))?.foto_rumah ?? '';
}

// Cache riwayat per nomor sambung: dipakai card (tampilan riwayat terbaru) dan pencarian.
// Dikosongkan setiap data segel dimuat ulang.
const historyRowsCache = new Map<string, SegelHistory[]>();
const historyPending = new Map<string, Promise<SegelHistory[]>>();

function getHistory(nsb: string): Promise<SegelHistory[]> {
    const cached = historyRowsCache.get(nsb);
    if (cached) return Promise.resolve(cached);

    // request yang sedang berjalan dipakai bersama, jadi satu nomor sambung tidak diambil dua kali
    const pending = historyPending.get(nsb);
    if (pending) return pending;

    const request = fetchHistoryRows(nsb)
        .then((rows) => {
            historyRowsCache.set(nsb, rows);
            return rows;
        })
        .finally(() => {
            historyPending.delete(nsb);
        });

    historyPending.set(nsb, request);
    return request;
}

function clearHistoryCache() {
    historyRowsCache.clear();
    historyPending.clear();
}

function rowMatches(row: { nsb?: string; nama?: string; alamat?: string }, keyword: string): boolean {
    return [row.nsb, row.nama, row.alamat].some((value) => String(value ?? '').toLowerCase().includes(keyword));
}

/**
 * Card satu segel per nomor sambung. Nama, alamat, dan foto rumah diambil dari
 * riwayat terbaru nomor sambung itu (diambil saat card mendekati layar).
 */
function SegelCard({ item, olderMatch, onOpen }: { item: Segel; olderMatch: boolean; onOpen: () => void }) {
    const ref = useRef<HTMLButtonElement>(null);
    const [rows, setRows] = useState<SegelHistory[] | null>(historyRowsCache.get(item.nsb) ?? null);

    useEffect(() => {
        if (rows) return;
        const node = ref.current;
        if (!node) return;

        let cancelled = false;

        const load = async () => {
            try {
                const data = await getHistory(item.nsb);
                if (!cancelled) setRows(data);
            } catch {
                // gagal memuat riwayat: tetap tampilkan data dari daftar
                if (!cancelled) setRows([]);
            }
        };

        // Riwayat baru diambil saat card mendekati layar, supaya tidak banyak request sekaligus
        if (typeof IntersectionObserver === 'undefined') {
            void load();
            return () => {
                cancelled = true;
            };
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    observer.disconnect();
                    void load();
                }
            },
            { rootMargin: '200px' },
        );
        observer.observe(node);

        return () => {
            cancelled = true;
            observer.disconnect();
        };
    }, [item.nsb, rows]);

    const latest = rows ? getLatestRow(rows) : null;
    const nama = latest?.nama || item.nama;
    const alamat = latest?.alamat || item.alamat;
    const foto = (rows ? getLatestHousePhoto(rows) : '') || item.foto_rumah || '';
    const status = latest?.status;

    return (
        <button
            ref={ref}
            type="button"
            onClick={onOpen}
            className="flex w-full items-center gap-3 rounded-2xl bg-white p-3 text-left shadow-sm transition-colors active:bg-gray-50"
        >
            <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gray-100 text-gray-400">
                {foto ? (
                    <img
                        src={STORAGE_URL + encodeURIComponent(foto)}
                        alt={'Foto rumah terbaru ' + item.nsb}
                        loading="lazy"
                        className="h-full w-full object-cover"
                    />
                ) : rows ? (
                    <ImageOff className="h-6 w-6" />
                ) : (
                    <div className="h-full w-full animate-pulse bg-gray-200" />
                )}
            </div>
            <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-gray-900">{nama || '-'}</p>
                <p className="mt-0.5 text-sm text-blue-700">No. Sambung: {item.nsb}</p>
                <p className={`mt-0.5 text-sm text-${status === "AKTIF" ? "green" : "red"}-700`}>Status {status}</p>
                <p className="mt-1 line-clamp-2 text-sm text-gray-600">{alamat || '-'}</p>
                <p className="mt-1 text-xs text-gray-500">Jumlah: {item.jumlah}</p>
                {olderMatch && (
                    <span className="mt-1 inline-block rounded bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                        Cocok di riwayat lama
                    </span>
                )}
            </div>
            <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" />
        </button>
    );
}

/** Kotak foto di form: menampilkan hasil foto, dan membuka kamera aplikasi saat ditekan. */
function PhotoInput({
    label,
    file,
    onOpenCamera,
    required = false,
}: {
    label: string;
    file: File | null;
    onOpenCamera: () => void;
    required?: boolean;
}) {
    const preview = useMemo(() => (file ? URL.createObjectURL(file) : ''), [file]);

    useEffect(() => {
        return () => {
            if (preview) URL.revokeObjectURL(preview);
        };
    }, [preview]);

    return (
        <div>
            <span className="mb-2 block text-sm font-medium text-gray-700">
                {label}{required && <span className="text-red-500"> *</span>}
            </span>
            <button
                type="button"
                onClick={onOpenCamera}
                aria-label={file ? `Foto ulang ${label}` : `Ambil ${label}`}
                className="flex min-h-28 w-full items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-gray-300 bg-gray-50"
            >
                {preview ? (
                    <img src={preview} alt={label} className="h-36 w-full object-cover" />
                ) : (
                    <span className="flex flex-col items-center gap-2 text-sm text-gray-500">
                        <Camera className="h-6 w-6" />
                        Ambil foto
                    </span>
                )}
            </button>
            {preview && (
                <button type="button" onClick={onOpenCamera} className="mt-2 text-xs font-semibold text-blue-700">
                    Foto ulang
                </button>
            )}
        </div>
    );
}

interface CameraFix {
    lat: number;
    lng: number;
    accuracy: number;
}

// Sisi terpanjang foto hasil kamera (px), supaya ukuran file tetap wajar saat diupload
const MAX_PHOTO_SIDE = 1920;

// Teks yang tampil di layar kamera dan dicetak di bagian bawah foto
function buildStampLines(label: string, fix: CameraFix | null, when: Date, waiting: boolean): string[] {
    const lines = [label];

    if (fix) {
        lines.push(`Latitude: ${fix.lat.toFixed(6)}`);
        lines.push(`Longitude: ${fix.lng.toFixed(6)}`);
        lines.push(`Akurasi: ±${Math.round(fix.accuracy)} m`);
    } else {
        lines.push(waiting ? 'Mencari lokasi...' : 'Lokasi tidak tersedia');
    }

    lines.push(when.toLocaleString('id-ID'));
    return lines;
}

/**
 * Kamera di dalam aplikasi. Layar kamera menampilkan latitude/longitude secara langsung,
 * dan teks yang sama dicetak di bagian bawah foto saat diambil.
 */
function CameraCapture({
    label,
    onCapture,
    onClose,
}: {
    label: string;
    onCapture: (file: File) => void;
    onClose: () => void;
}) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);

    const [cameraReady, setCameraReady] = useState(false);
    const [cameraError, setCameraError] = useState('');
    const [fix, setFix] = useState<CameraFix | null>(null);
    const [geoError, setGeoError] = useState('');
    const [now, setNow] = useState(() => new Date());
    const [shot, setShot] = useState<{ file: File; url: string } | null>(null);

    // Buka kamera belakang
    useEffect(() => {
        let cancelled = false;

        const start = async () => {
            if (!navigator.mediaDevices?.getUserMedia) {
                setCameraError(
                    window.isSecureContext
                        ? 'Browser ini tidak mendukung kamera.'
                        : 'Kamera hanya bisa dipakai lewat HTTPS.',
                );
                return;
            }

            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
                    audio: false,
                });

                if (cancelled) {
                    stream.getTracks().forEach((track) => track.stop());
                    return;
                }

                streamRef.current = stream;
                const video = videoRef.current;
                if (video) {
                    video.srcObject = stream;
                    await video.play().catch(() => undefined);
                }
                setCameraReady(true);
            } catch (err) {
                if (cancelled) return;

                const name = err instanceof DOMException ? err.name : '';
                setCameraError(
                    name === 'NotAllowedError'
                        ? 'Izin kamera ditolak. Aktifkan izin kamera di browser.'
                        : name === 'NotFoundError'
                          ? 'Kamera tidak ditemukan di perangkat ini.'
                          : 'Kamera tidak bisa dibuka.',
                );
            }
        };

        void start();

        return () => {
            cancelled = true;
            streamRef.current?.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        };
    }, []);

    // Pantau lokasi selama kamera terbuka
    useEffect(() => {
        if (!navigator.geolocation) {
            setGeoError('Perangkat ini tidak mendukung lokasi.');
            return;
        }

        const watchId = navigator.geolocation.watchPosition(
            (position) => {
                setFix({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    accuracy: position.coords.accuracy,
                });
                setGeoError('');
            },
            (err) => {
                setGeoError(err.code === err.PERMISSION_DENIED ? 'Izin lokasi ditolak.' : 'Lokasi belum tersedia.');
            },
            { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 },
        );

        return () => navigator.geolocation.clearWatch(watchId);
    }, []);

    // Jam di layar kamera
    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    // Bersihkan URL preview hasil foto
    useEffect(() => {
        return () => {
            if (shot) URL.revokeObjectURL(shot.url);
        };
    }, [shot]);

    const waitingLocation = !fix && !geoError;
    // Foto baru bisa diambil setelah lokasi didapat (atau lokasi memang gagal/ditolak)
    const canCapture = cameraReady && !waitingLocation;

    const capture = () => {
        const video = videoRef.current;
        if (!video || !video.videoWidth) return;

        const scale = Math.min(1, MAX_PHOTO_SIDE / Math.max(video.videoWidth, video.videoHeight));
        const width = Math.round(video.videoWidth * scale);
        const height = Math.round(video.videoHeight * scale);

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const context = canvas.getContext('2d');
        if (!context) return;

        context.drawImage(video, 0, 0, width, height);

        // Cetak keterangan lokasi di bagian bawah foto
        const lines = buildStampLines(label, fix, new Date(), false);
        const fontSize = Math.max(14, Math.round(width / 38));
        const padding = Math.round(fontSize * 0.7);
        const lineHeight = Math.round(fontSize * 1.35);
        const boxHeight = lines.length * lineHeight + padding * 2;

        context.fillStyle = 'rgba(0, 0, 0, 0.55)';
        context.fillRect(0, height - boxHeight, width, boxHeight);
        context.fillStyle = '#ffffff';
        context.font = `600 ${fontSize}px sans-serif`;
        context.textBaseline = 'top';
        lines.forEach((line, index) => {
            context.fillText(line, padding, height - boxHeight + padding + index * lineHeight);
        });

        const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        canvas.toBlob(
            (blob) => {
                if (!blob) return;
                const file = new File([blob], `${slug}-${Date.now()}.jpg`, { type: 'image/jpeg' });
                setShot({ file, url: URL.createObjectURL(file) });
            },
            'image/jpeg',
            0.85,
        );
    };

    const useShot = () => {
        if (!shot) return;
        onCapture(shot.file);
        onClose();
    };

    return (
        <div className="absolute inset-0 z-[59] flex flex-col bg-black text-white">
            <div className="flex shrink-0 items-center justify-between px-4 py-3">
                <p className="text-sm font-medium">{label}</p>
                <button type="button" onClick={onClose} aria-label="Tutup kamera" className="rounded-lg p-2">
                    <X className="h-5 w-5" />
                </button>
            </div>

            <div className="relative min-h-0 flex-1">
                <video
                    ref={videoRef}
                    playsInline
                    muted
                    autoPlay
                    className={`h-full w-full object-contain ${shot ? 'hidden' : ''}`}
                />
                {shot && <img src={shot.url} alt={`Hasil ${label}`} className="h-full w-full object-contain" />}

                {cameraError && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black p-6 text-center">
                        <p className="text-sm">{cameraError}</p>
                        <label className="cursor-pointer rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-gray-900">
                            Pakai kamera perangkat
                            <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="sr-only"
                                onChange={(event) => {
                                    const picked = event.target.files?.[0];
                                    if (picked) {
                                        onCapture(picked);
                                        onClose();
                                    }
                                }}
                            />
                        </label>
                        <p className="text-xs text-gray-400">Foto dari cara ini tidak memakai cap lokasi.</p>
                    </div>
                )}

                {/* Keterangan lokasi langsung di layar kamera */}
                {!shot && !cameraError && (
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-black/55 px-4 py-2 text-xs leading-5">
                        {buildStampLines(label, fix, now, waitingLocation).map((line, index) => (
                            <p key={index} className={index === 0 ? 'font-semibold' : ''}>{line}</p>
                        ))}
                    </div>
                )}
            </div>

            <div className="flex shrink-0 flex-col items-center gap-2 px-5 py-4">
                {shot ? (
                    <div className="flex w-full gap-3">
                        <button
                            type="button"
                            onClick={() => setShot(null)}
                            className="flex-1 rounded-xl border border-white/60 px-4 py-3 text-sm font-semibold"
                        >
                            Ulangi
                        </button>
                        <button
                            type="button"
                            onClick={useShot}
                            className="flex-1 rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white"
                        >
                            Gunakan foto
                        </button>
                    </div>
                ) : (
                    <>
                        <button
                            type="button"
                            onClick={capture}
                            disabled={!canCapture}
                            aria-label="Ambil foto"
                            className="flex h-16 w-16 items-center justify-center rounded-full border-4 border-white disabled:opacity-40"
                        >
                            <span className="h-12 w-12 rounded-full bg-white" />
                        </button>
                        {cameraReady && waitingLocation && (
                            <p className="text-xs text-gray-300">Menunggu lokasi sebelum bisa mengambil foto...</p>
                        )}
                        {geoError && !fix && <p className="text-xs text-amber-300">{geoError} Foto diambil tanpa koordinat.</p>}
                    </>
                )}
            </div>
        </div>
    );
}

/** Foto yang bisa ditekan untuk dibuka di preview layar penuh. */
function PhotoButton({
    path,
    label,
    imgClassName,
    onOpen,
}: {
    path: string;
    label: string;
    imgClassName: string;
    onOpen: (path: string, label: string) => void;
}) {
    return (
        <button
            type="button"
            onClick={() => onOpen(path, label)}
            aria-label={`Lihat ${label}`}
            className="block w-full cursor-zoom-in"
        >
            <img src={fotoUrl(path)} alt={label} className={imgClassName} />
        </button>
    );
}

/**
 * Slot "Segel sesudah" untuk riwayat yang fotonya masih kosong:
 * pilih foto, lihat preview, lalu Simpan (upload) atau Batal.
 */
function SesudahUploader({ onUpload }: { onUpload: (file: File) => Promise<void> }) {
    const [file, setFile] = useState<File | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const preview = useMemo(() => (file ? URL.createObjectURL(file) : ''), [file]);

    useEffect(() => {
        return () => {
            if (preview) URL.revokeObjectURL(preview);
        };
    }, [preview]);

    const saveFotoSesudah = async () => {
        if (!file) return;
        setSaving(true);
        setError('');

        try {
            await onUpload(file);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Gagal menyimpan foto.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            {preview ? (
                <img src={preview} alt="Calon foto segel sesudah" className="h-28 w-full rounded-lg object-cover" />
            ) : (
                <label className="flex h-28 w-full cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-blue-300 bg-blue-50 text-xs font-medium text-blue-700">
                    <ImagePlus className="h-5 w-5" />
                    Tambah foto
                    <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="sr-only"
                        onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                    />
                </label>
            )}

            {file && (
                <div className="mt-2 flex gap-2">
                    <button
                        type="button"
                        onClick={() => {
                            setFile(null);
                            setError('');
                        }}
                        disabled={saving}
                        className="flex-1 rounded-lg border border-gray-300 px-2 py-1.5 text-xs font-medium text-gray-700 disabled:opacity-50"
                    >
                        Batal
                    </button>
                    <button
                        type="button"
                        onClick={() => void saveFotoSesudah()}
                        disabled={saving}
                        className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-blue-600 px-2 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                    >
                        {saving ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : null}
                        {saving ? 'Menyimpan' : 'Simpan'}
                    </button>
                </div>
            )}

            {error && <p role="alert" className="mt-1 text-xs text-red-600">{error}</p>}
        </div>
    );
}

const MIN_SEARCH_CHARS = 2;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Kolom teks dengan saran pelanggan (autocomplete).
 * Dipakai untuk "Nomor Sambung" dan "Nama Pelanggan": ketik sebagian, pilih saran,
 * lalu onSelect dipanggil dengan data pelanggan lengkap.
 */
function CustomerAutocomplete({
    label,
    value,
    onChange,
    onSelect,
    placeholder,
    isDisabled,
    disabledNote = 'Tidak bisa dipilih',
}: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    onSelect: (pelanggan: Pelanggan) => void;
    placeholder?: string;
    // saran yang mengembalikan true ditandai dan tidak bisa dipilih
    isDisabled?: (pelanggan: Pelanggan) => boolean;
    disabledNote?: string;
}) {
    const [options, setOptions] = useState<Pelanggan[]>([]);
    const [open, setOpen] = useState(false);
    const [searching, setSearching] = useState(false);
    const [active, setActive] = useState(-1);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const abortRef = useRef<AbortController | null>(null);

    // Bersihkan timer dan request saat komponen ditutup
    useEffect(() => {
        return () => {
            clearTimeout(timerRef.current);
            abortRef.current?.abort();
        };
    }, []);

    // Tutup daftar saat menekan di luar komponen
    useEffect(() => {
        const handleOutside = (event: MouseEvent | TouchEvent) => {
            if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handleOutside);
        document.addEventListener('touchstart', handleOutside);
        return () => {
            document.removeEventListener('mousedown', handleOutside);
            document.removeEventListener('touchstart', handleOutside);
        };
    }, []);

    const cancelSearch = () => {
        clearTimeout(timerRef.current);
        abortRef.current?.abort();
    };

    const search = (text: string) => {
        cancelSearch();

        const keyword = text.trim();
        if (keyword.length < MIN_SEARCH_CHARS) {
            setOptions([]);
            setOpen(false);
            setSearching(false);
            return;
        }

        setOpen(true);
        setSearching(true);

        timerRef.current = setTimeout(async () => {
            const controller = new AbortController();
            abortRef.current = controller;

            try {
                const response = await fetch(
                    `${API_URL}/hublang/segel/pelanggan?data=${encodeURIComponent(keyword)}`,
                    { headers: { Authorization: API_KEY }, signal: controller.signal },
                );
                const result = await response.json();
                console.log(result);
                const rows = Array.isArray(result) ? result : result?.data;

                setOptions(Array.isArray(rows) ? rows : []);
                setActive(-1);
            } catch (err) {
                if (err instanceof DOMException && err.name === 'AbortError') return;
                setOptions([]);
            } finally {
                if (!controller.signal.aborted) setSearching(false);
            }
        }, SEARCH_DEBOUNCE_MS);
    };

    const blocked = (pelanggan: Pelanggan) => Boolean(isDisabled?.(pelanggan));

    const pick = (pelanggan: Pelanggan) => {
        if (blocked(pelanggan)) return;

        cancelSearch();
        onSelect(pelanggan);
        setOptions([]);
        setOpen(false);
        setSearching(false);
        setActive(-1);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!open) return;

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActive((index) => Math.min(index + 1, options.length - 1));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActive((index) => Math.max(index - 1, 0));
        } else if (event.key === 'Enter' && active >= 0 && options[active]) {
            event.preventDefault();
            pick(options[active]);
        } else if (event.key === 'Escape') {
            setOpen(false);
        }
    };

    return (
        <div ref={wrapperRef} className="relative">
            <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
                <input
                    required
                    value={value}
                    placeholder={placeholder}
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={open}
                    aria-autocomplete="list"
                    onChange={(event) => {
                        onChange(event.target.value);
                        search(event.target.value);
                    }}
                    onFocus={() => {
                        if (options.length > 0) setOpen(true);
                    }}
                    onKeyDown={handleKeyDown}
                    className="w-full rounded-xl border border-gray-300 px-3 py-3"
                />
            </label>

            {open && (
                <ul
                    role="listbox"
                    className="absolute left-0 right-0 top-full z-20 mt-1 max-h-60 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg"
                >
                    {searching ? (
                        <li className="flex items-center gap-2 px-3 py-3 text-sm text-gray-500">
                            <LoaderCircle className="h-4 w-4 animate-spin" />
                            Mencari...
                        </li>
                    ) : options.length === 0 ? (
                        <li className="px-3 py-3 text-sm text-gray-500">Pelanggan tidak ditemukan.</li>
                    ) : (
                        options.map((option, index) => (
                            <li key={option.nsb} role="option" aria-selected={index === active} aria-disabled={blocked(option)}>
                                <button
                                    type="button"
                                    disabled={blocked(option)}
                                    // cegah input kehilangan fokus sebelum pilihan diproses
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => pick(option)}
                                    className={`block w-full px-3 py-2 text-left ${
                                        blocked(option)
                                            ? 'cursor-not-allowed bg-gray-50 opacity-70'
                                            : index === active
                                              ? 'bg-blue-50'
                                              : 'hover:bg-gray-50'
                                    }`}
                                >
                                    <span className="block text-sm font-medium text-gray-900">{option.nama}</span>
                                    <span className="block text-xs text-gray-500">
                                        {option.nsb}
                                        {option.alamat
                                            ? ` · ${option.alamat} · ${option.stat_smb === '30' ? 'Aktif' : option.stat_smb === '40' ? 'Cabut Meter' : 'Segel   '}`
                                            : ''}
                                    </span>
                                    {blocked(option) && (
                                        <span className="mt-1 inline-block rounded bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
                                            {disabledNote}
                                        </span>
                                    )}
                                </button>
                            </li>
                        ))
                    )}
                </ul>
            )}
        </div>
    );
}

/**
 * Layar pemilih lokasi: peta OpenStreetMap dengan satu pin.
 * Tekan peta atau geser pin untuk mengubah titik, atau pakai tombol "Lokasi saya" (GPS).
 */
function LocationPicker({
    initial,
    onCancel,
    onConfirm,
}: {
    initial: GeoPoint | null;
    onCancel: () => void;
    onConfirm: (point: GeoPoint) => void;
}) {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<L.Map | null>(null);
    const markerRef = useRef<L.Marker | null>(null);

    const [position, setPosition] = useState<GeoPoint | null>(initial);
    const [locating, setLocating] = useState(false);
    const [geoError, setGeoError] = useState('');

    const placeMarker = (point: GeoPoint, zoomIn: boolean) => {
        const map = mapRef.current;
        if (!map) return;

        if (markerRef.current) {
            markerRef.current.setLatLng([point.lat, point.lng]);
        } else {
            const marker = L.marker([point.lat, point.lng], { draggable: true, icon: PIN_ICON }).addTo(map);
            marker.on('dragend', () => {
                const { lat, lng } = marker.getLatLng();
                setPosition({ lat, lng });
            });
            markerRef.current = marker;
        }

        if (zoomIn) map.setView([point.lat, point.lng], Math.max(map.getZoom(), 17));
        setPosition(point);
    };

    const locate = () => {
        setGeoError('');

        if (!navigator.geolocation) {
            setGeoError('Perangkat ini tidak mendukung lokasi otomatis. Tekan peta untuk memilih manual.');
            return;
        }

        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            (result) => {
                setLocating(false);
                placeMarker({ lat: result.coords.latitude, lng: result.coords.longitude }, true);
            },
            (err) => {
                setLocating(false);
                if (err.code === err.PERMISSION_DENIED) {
                    setGeoError('Izin lokasi ditolak. Aktifkan izin lokasi di browser, atau tekan peta untuk memilih manual.');
                } else if (err.code === err.TIMEOUT) {
                    setGeoError('Pencarian lokasi terlalu lama. Coba lagi, atau tekan peta untuk memilih manual.');
                } else {
                    setGeoError('Lokasi belum tersedia. Tekan peta untuk memilih manual.');
                }
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
        );
    };

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const center = initial ?? DEFAULT_MAP_CENTER;
        const map = L.map(container).setView([center.lat, center.lng], initial ? 17 : 13);

        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; OpenStreetMap',
        }).addTo(map);

        map.on('click', (event: L.LeafletMouseEvent) => {
            placeMarker({ lat: event.latlng.lat, lng: event.latlng.lng }, false);
        });

        mapRef.current = map;

        // Sudah ada titik -> tampilkan; belum ada -> langsung coba ambil lokasi saat ini
        if (initial) placeMarker(initial, false);
        else locate();

        // Hitung ulang ukuran peta setelah layar pemilih tampil penuh
        const timer = setTimeout(() => map.invalidateSize(), 100);

        return () => {
            clearTimeout(timer);
            map.remove();
            mapRef.current = null;
            markerRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="absolute inset-0 z-[57] flex flex-col bg-white">
            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-5 pb-3 pt-5">
                <div className="min-w-0">
                    <h2 className="text-xl font-bold">Pilih Lokasi</h2>
                    <p className="mt-1 text-sm text-gray-500">Tekan peta atau geser pin untuk mengubah titik.</p>
                </div>
                <button type="button" onClick={onCancel} aria-label="Tutup peta" className="shrink-0 rounded-lg p-2 text-gray-500">
                    <X className="h-5 w-5" />
                </button>
            </div>

            <div className="relative isolate min-h-0 flex-1">
                <div ref={containerRef} className="h-full w-full" />
            </div>

            <div className="shrink-0 space-y-2 border-t border-gray-200 bg-white px-5 py-3">
                <p className="text-xs text-gray-600">
                    {position
                        ? `Titik terpilih: ${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}`
                        : 'Belum ada titik yang dipilih.'}
                </p>
                {geoError && <p role="alert" className="text-xs text-red-600">{geoError}</p>}
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={locate}
                        disabled={locating}
                        className="flex items-center justify-center gap-2 rounded-xl border border-blue-600 px-4 py-3 text-sm font-semibold text-blue-700 disabled:opacity-60"
                    >
                        {locating ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />}
                        Lokasi saya
                    </button>
                    <button
                        type="button"
                        onClick={() => position && onConfirm(position)}
                        disabled={!position}
                        className="flex-1 rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
                    >
                        Gunakan titik ini
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function HomeMobileSegel() {
    const location = useLocation();
    const navigate = useNavigate();
    const routeState = location.state;
    const routeModalStack = getSegelModalStack(routeState);
    const modalStackRef = useRef<SegelModal[]>(routeModalStack);
    const [segelData, setSegelData] = useState<Segel[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [showForm, setShowForm] = useState(false);
    // 'baru' = tambah segel pelanggan baru, 'history' = tambah riwayat dari halaman detail
    const [formMode, setFormMode] = useState<'baru' | 'history'>('baru');
    const [selectedSegel, setSelectedSegel] = useState<Segel | null>(null);
    const [history, setHistory] = useState<SegelHistory[]>([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [previewImage, setPreviewImage] = useState<{ src: string; label: string } | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<DeleteTarget | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const [dataTake, setDataTake] = useState('');
    const [nama, setNama] = useState('');
    const [nsb, setNsb] = useState('');
    const [alamat, setAlamat] = useState('');
    const [mLalu, setMLalu] = useState('');
    const [fotoRumah, setFotoRumah] = useState<File | null>(null);
    const [meterSebelum, setMeterSebelum] = useState<File | null>(null);
    const [meterSesudah, setMeterSesudah] = useState<File | null>(null);
    const [meterAktif, setMeterAktif] = useState<File | null>(null);
    const [koordinat, setKoordinat] = useState<GeoPoint | null>(null);
    const [showMap, setShowMap] = useState(false);
    // kamera aplikasi untuk foto di form: label foto + fungsi yang menerima hasilnya
    const [camera, setCamera] = useState<{ label: string; onDone: (file: File) => void } | null>(null);
    // naik tiap ada riwayat baru yang masuk cache, supaya hasil pencarian dihitung ulang
    const [historyVersion, setHistoryVersion] = useState(0);
    const [searchingHistory, setSearchingHistory] = useState(false);

    const openModal = (modal: SegelModal) => {
        if (modalStackRef.current[modalStackRef.current.length - 1] === modal) return;
        const nextStack = [...modalStackRef.current, modal];
        modalStackRef.current = nextStack;
        const state = routeState && typeof routeState === 'object'
            ? routeState as Record<string, unknown>
            : {};
        navigate(location.pathname, {
            state: { ...state, segelModalStack: nextStack },
        });
    };

    const closeModal = (modal?: SegelModal, count = 1) => {
        const stack = modalStackRef.current;
        if (stack.length === 0 || (modal && stack[stack.length - 1] !== modal)) return;
        navigate(-count);
    };

    // Nomor sambung yang sudah punya data segel (tidak boleh ditambahkan lagi sebagai segel baru)
    const existingNsb = useMemo(
        () => new Set(segelData.map((item) => String(item.nsb).trim())),
        [segelData],
    );

    const fetchSegel = async () => {
        clearHistoryCache();
        setLoading(true);
        setError('');

        try {
            const response = await fetch(`${API_URL}/hublang/segel`, {
                headers: { Authorization: API_KEY },
            });
            const result: ApiResponse<Segel[]> = await response.json();

            if (!response.ok || result.status !== 200) {
                throw new Error(result.message || 'Gagal memuat data segel.');
            }

            // console.log(result.data);

            setSegelData(Array.isArray(result.data) ? result.data : []);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Gagal memuat data segel.');
            setSegelData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void fetchSegel();
    }, []);

    // Saat mencari: muat riwayat semua nomor sambung (maks. 5 request bersamaan) supaya
    // nama/alamat di riwayat lama ikut terdeteksi. Hasilnya di-cache, jadi pencarian berikutnya cepat.
    useEffect(() => {
        const keyword = search.trim();
        const missing = segelData.map((item) => item.nsb).filter((nsb) => !historyRowsCache.has(nsb));

        if (keyword.length < 2 || missing.length === 0) {
            setSearchingHistory(false);
            return;
        }

        let cancelled = false;

        const timer = setTimeout(async () => {
            setSearchingHistory(true);

            const queue = [...missing];
            let done = 0;

            const worker = async () => {
                while (!cancelled) {
                    const nsb = queue.shift();
                    if (!nsb) return;

                    try {
                        await getHistory(nsb);
                    } catch {
                        // nomor sambung yang gagal dilewati
                    }

                    done += 1;
                    if (!cancelled && done % 5 === 0) setHistoryVersion((version) => version + 1);
                }
            };

            await Promise.all(Array.from({ length: 5 }, worker));

            if (!cancelled) {
                setHistoryVersion((version) => version + 1);
                setSearchingHistory(false);
            }
        }, 300);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [search, segelData]);

    const openDetail = async (segel: Segel) => {
        setHistory([]);
        setLoadingHistory(true);
        setSelectedSegel(segel);
        openModal('detail');

        try {
            setHistory(await fetchHistoryRows(segel.nsb));
        } catch {
            setHistory([]);
        } finally {
            setLoadingHistory(false);
        }
    };

    // Tutup preview foto dengan tombol Esc
    useEffect(() => {
        if (!previewImage) return;

        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') closeModal('preview');
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [previewImage, closeModal]);

    const openPreview = (path: string, label: string) => {
        openModal('preview');
        setPreviewImage({ src: fotoUrl(path), label });
    };

    // Tambah foto meter sesudah pada satu riwayat, lalu muat ulang riwayatnya
    const uploadMeterSesudah = async (item: SegelHistory, file: File) => {
        if (!selectedSegel) return;

        const formData = new FormData();
        if (selectedSegel.nsb) formData.append('nsb', selectedSegel.nsb);
        if (item.id) formData.append('id_history', item.id);
        if (item.foto_meter) formData.append('foto_meter', JSON.stringify(item.foto_meter))
        formData.append('meter_sesudah', file);

        console.log(item.id, item.modified_at)

        const response = await fetch(API_URL + "/hublang/segel/sesudah", {
            method: 'PATCH',
            headers: { Authorization: API_KEY },
            body: formData,
        });
        const result = await response.json();

        console.log("Upload Meter Sesudah");
        console.log(result);

        if (!response.ok || (result.status && result.status !== 200 && result.status !== 201)) {
            throw new Error(result.message || 'Gagal menyimpan foto meter sesudah.');
        }

        setHistory(await fetchHistoryRows(selectedSegel.nsb));
    };

    // Dipanggil saat petugas memilih saran: isi nomor sambung, nama, dan alamat sekaligus
    const applyPelanggan = (pelanggan: Pelanggan) => {
        setDataTake(pelanggan.nsb);
        setDataTake(pelanggan.nama);
        setNama(pelanggan.nama);
        setNsb(pelanggan.nsb);
        setAlamat(pelanggan.alamat ?? '');
    };

    const resetForm = useCallback(() => {
        setDataTake('');
        setNama('');
        setNsb('');
        setAlamat('');
        setMLalu('');
        setFotoRumah(null);
        setMeterSebelum(null);
        setMeterSesudah(null);
        setKoordinat(null);
        setShowMap(false);
        setCamera(null);
    }, []);

    const dismissModal = useCallback((modal: SegelModal) => {
        switch (modal) {
            case 'detail':
                setSelectedSegel(null);
                setHistory([]);
                break;
            case 'form':
                setShowForm(false);
                resetForm();
                break;
            case 'map':
                setShowMap(false);
                break;
            case 'camera':
                setCamera(null);
                break;
            case 'delete':
                setConfirmDelete(null);
                setDeleteError('');
                break;
            case 'preview':
                setPreviewImage(null);
                break;
        }
    }, [resetForm]);

    useEffect(() => {
        const nextStack = getSegelModalStack(routeState);
        const currentStack = modalStackRef.current;

        if (nextStack.length < currentStack.length) {
            currentStack.slice(nextStack.length).reverse().forEach(dismissModal);
        }

        modalStackRef.current = nextStack;
    }, [dismissModal, location.key, routeState]);

    const openNewForm = () => {
        resetForm();
        setFormMode('baru');
        setError('');
        openModal('form');
        setShowForm(true);
    };

    // Tambah history: data pelanggan diambil dari detail, foto dan meter diisi ulang
    const openHistoryForm = () => {
        if (!selectedSegel) return;

        resetForm();
        setFormMode('history');
        setDataTake(selectedSegel.nama);
        setNama(selectedSegel.nama);
        setNsb(selectedSegel.nsb);
        setAlamat(selectedSegel.alamat ?? '');
        // Lokasi terakhir dijadikan isian awal, petugas bisa mengubahnya lewat peta
        setKoordinat(latestKoordinat(history));
        setError('');
        openModal('form');
        setShowForm(true);
    };

    const openCamera = (cameraOptions: { label: string; onDone: (file: File) => void }) => {
        openModal('camera');
        setCamera(cameraOptions);
    };

    const submitForm = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setSaving(true);
        setError('');

        try {
            if (formMode === 'history' && !selectedSegel) {
                throw new Error('Pilih data segel terlebih dahulu.');
            }

            if (!fotoRumah) throw new Error('Foto rumah wajib diambil.');
            if (!meterSebelum && !meterAktif) throw new Error('Foto segel sebelum wajib diambil.');

            if (formMode === 'baru' && existingNsb.has(nsb.trim())) {
                throw new Error(
                    `Nomor sambung ${nsb.trim()} sudah memiliki data segel, tidak bisa ditambahkan. Gunakan Tambah History dari detail segel.`,
                );
            }

            const formData = new FormData();
            formData.append('nama', nama.trim());
            formData.append('nsb', nsb.trim());
            formData.append('alamat', alamat.trim());
            formData.append('m_lalu', mLalu.trim());
            formData.append('status', meterAktif ? "AKTIF" : "SEGEL")
            if (koordinat) formData.append('koordinat', `${koordinat.lat.toFixed(6)},${koordinat.lng.toFixed(6)}`);

            if (selectedSegel) formData.append('id_segel', selectedSegel.id);
            if (fotoRumah) formData.append('foto_rumah', fotoRumah);
            if (meterAktif) formData.append('meter_sebelum', meterAktif);
            else if (meterSebelum) formData.append('meter_sebelum', meterSebelum);
            if (meterSesudah) formData.append('meter_sesudah', meterSesudah);

            const apiUrl = formMode === 'history' ? `${API_URL}/hublang/segel/history` : `${API_URL}/hublang/segel`;

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { Authorization: API_KEY },
                body: formData,
            });
            const result = await response.json();

            if (!response.ok || (result.status && result.status !== 200 && result.status !== 201)) {
                throw new Error(result.message || 'Gagal menyimpan data segel.');
            }

            const historyNsb = formMode === 'history' ? selectedSegel?.nsb : undefined;

            closeModal('form');
            await fetchSegel();

            // Kalau ditambah dari detail, muat ulang riwayat supaya data baru langsung tampil
            if (historyNsb) {
                try {
                    setHistory(await fetchHistoryRows(historyNsb));
                } catch {
                    // riwayat lama tetap ditampilkan
                }
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Gagal menyimpan data segel.');
        } finally {
            setSaving(false);
        }
    };

    const closeConfirmDelete = () => {
        if (deleting) return;
        closeModal('delete');
    };

    const performDelete = async () => {
        const target = confirmDelete;
        if (!target || !selectedSegel) return;

        setDeleting(true);
        setDeleteError('');

        try {
            const id = target.kind === 'segel' ? selectedSegel.id : target.item.id;
            if (!id) throw new Error('Data ini tidak memiliki id, jadi tidak bisa dihapus.');

            const response = await fetch(
                target.kind === 'segel' ? deleteSegelUrl(id) : deleteHistoryUrl(id),
                { method: 'DELETE', headers: { Authorization: API_KEY } },
            );
            // respons kosong (mis. 204) dianggap berhasil selama status HTTP-nya ok
            const result = await response.json().catch(() => ({}));

            if (!response.ok || (result.status && ![200, 201, 204].includes(result.status))) {
                throw new Error(result.message || 'Gagal menghapus data.');
            }

            closeModal('delete', target.kind === 'segel' ? 2 : 1);

            if (target.kind === 'segel') {
                await fetchSegel();
            } else {
                const nsbNow = selectedSegel.nsb;
                await fetchSegel();
                setHistory(await fetchHistoryRows(nsbNow));
            }
        } catch (err) {
            setDeleteError(err instanceof Error ? err.message : 'Gagal menghapus data.');
        } finally {
            setDeleting(false);
        }
    };

    // Satu card per nomor sambung. Cocok kalau kata kunci ada di yang tampil di card
    // (nsb + nama/alamat riwayat terbaru), atau di riwayat lama nomor sambung itu.
    const filteredSegel = useMemo(() => {
        const keyword = search.trim().toLowerCase();
        if (!keyword) return segelData.map((item) => ({ item, olderMatch: false }));

        const result: { item: Segel; olderMatch: boolean }[] = [];

        for (const item of segelData) {
            const rows = historyRowsCache.get(item.nsb) ?? [];
            const latest = getLatestRow(rows);

            const shown = rowMatches(
                { nsb: item.nsb, nama: latest?.nama || item.nama, alamat: latest?.alamat || item.alamat },
                keyword,
            );

            if (shown) {
                result.push({ item, olderMatch: false });
            } else if (rowMatches(item, keyword) || rows.some((row) => rowMatches(row, keyword))) {
                result.push({ item, olderMatch: true });
            }
        }

        return result;
        // historyVersion: cache riwayat berubah di luar state React
    }, [segelData, search, historyVersion]);

    return (
        <main className="relative flex h-full min-h-0 flex-1 flex-col bg-gray-50">
            {/* Bagian atas (tetap): judul, tombol tambah, pencarian */}
            <div className="shrink-0 px-4 pb-3 pt-5">
                <header className="mb-4 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <h1 className="text-2xl font-bold text-gray-900">Data Segel</h1>
                        <p className="mt-1 text-sm text-gray-500">Daftar dan riwayat penyegelan</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => void fetchSegel()}
                        disabled={loading}
                        aria-label="Muat ulang"
                        className="shrink-0 rounded-xl border border-gray-200 bg-white p-3 text-gray-700 shadow-sm disabled:opacity-50"
                    >
                        <RotateCcw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </header>

                <button
                    type="button"
                    onClick={openNewForm}
                    className="mb-3 flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 font-semibold text-white shadow-sm hover:bg-blue-700 active:bg-blue-800"
                >
                    <Plus className="h-5 w-5" />
                    Tambah Segel
                </button>

                <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Cari nama, alamat, atau nomor sambung"
                    className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500"
                />
                {searchingHistory && (
                    <p className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                        <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
                        Mencari di riwayat...
                    </p>
                )}
            </div>

            {/* Bagian tengah (scroll): error + daftar segel */}
            <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
            {error && !showForm && (
                <p role="alert" className="mb-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">
                    {error}
                </p>
            )}

            {loading ? (
                <div className="flex justify-center py-12 text-gray-500">
                    <LoaderCircle className="mr-2 h-5 w-5 animate-spin" />
                    Memuat data...
                </div>
            ) : filteredSegel.length === 0 ? (
                <p className="rounded-xl bg-white p-6 text-center text-sm text-gray-500">
                    {searchingHistory ? 'Mencari di riwayat...' : 'Data segel tidak ditemukan.'}
                </p>
            ) : (
                <div className="space-y-3">
                    {filteredSegel.map(({ item, olderMatch }) => (
                        <SegelCard
                            key={item.id}
                            item={item}
                            olderMatch={olderMatch}
                            onOpen={() => void openDetail(item)}
                        />
                    ))}
                </div>
            )}
            </div>

            {showForm && (
                <div className="absolute inset-0 z-[55] overflow-y-auto bg-black/50">
                    <section className="mx-auto min-h-full max-w-lg bg-white p-5">
                        <div className="mb-5 flex items-center justify-between">
                            <h2 className="text-xl font-bold">{formMode === 'history' ? 'Tambah History Segel' : 'Tambah Data Segel'}</h2>
                            <button
                                type="button"
                                onClick={() => closeModal('form')}
                                aria-label="Tutup form"
                                className="rounded-lg p-2 text-gray-500"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <form onSubmit={submitForm} className="space-y-4">
                            {formMode === 'history' ? (
                                <>
                                    <label className="block">
                                        <span className="mb-1 block text-sm font-medium text-gray-700">Nama</span>
                                        <input required value={nama} onChange={(event) => setNama(event.target.value)} className="w-full rounded-xl border border-gray-300 px-3 py-3" />
                                    </label>
                                    <label className="block">
                                        <span className="mb-1 block text-sm font-medium text-gray-700">No. SMB</span>
                                        <input required value={nsb} onChange={(event) => setNsb(event.target.value)} className="w-full rounded-xl border border-gray-300 px-3 py-3" />
                                    </label>
                                    <label className="block">
                                        <span className="mb-1 block text-sm font-medium text-gray-700">Alamat</span>
                                        <textarea required value={alamat} onChange={(event) => setAlamat(event.target.value)} rows={2} className="w-full rounded-xl border border-gray-300 px-3 py-3" />
                                    </label>
                                </>
                            ) : (
                                <>
                                <CustomerAutocomplete
                                    label="Data Pelanggan"
                                    placeholder="Masukkan Nama atau Nomor Sambung"
                                    value={dataTake}
                                    onChange={setDataTake}
                                    onSelect={applyPelanggan}
                                    isDisabled={(pelanggan) => existingNsb.has(String(pelanggan.nsb).trim())}
                                    disabledNote="Sudah ada data segel · tidak bisa ditambahkan"
                                />
                                {
                                    nama !== '' && nsb != '' ?
                                    <>
                                        <label className="block">
                                            <span className="mb-1 block text-sm font-medium text-gray-700">Nama</span>
                                            <input required value={nama} onChange={(event) => setNama(event.target.value)} className="w-full rounded-xl border border-gray-300 px-3 py-3" disabled/>
                                        </label>
                                        <label className="block">
                                            <span className="mb-1 block text-sm font-medium text-gray-700">No. SMB</span>
                                            <input required value={nsb} onChange={(event) => setNsb(event.target.value)} className="w-full rounded-xl border border-gray-300 px-3 py-3" disabled/>
                                        </label>
                                    </>
                                    :
                                    ''
                                }
                            
                                <label className="block">
                                    <span className="mb-1 block text-sm font-medium text-gray-700">Alamat</span>
                                    <textarea required value={alamat} onChange={(event) => setAlamat(event.target.value)} rows={2} className="w-full rounded-xl border border-gray-300 px-3 py-3" />
                                </label>
                                </>
                            )}
                            <label className="block">
                                <span className="mb-1 block text-sm font-medium text-gray-700">Meter Kini</span>
                                <input required inputMode="decimal" value={mLalu} onChange={(event) => setMLalu(event.target.value)} className="w-full rounded-xl border border-gray-300 px-3 py-3" />
                            </label>

                            <div>
                                <span className="mb-1 block text-sm font-medium text-gray-700">Lokasi</span>
                                <button
                                    type="button"
                                    onClick={() => {
                                        openModal('map');
                                        setShowMap(true);
                                    }}
                                    className="flex w-full items-center gap-3 rounded-xl border border-gray-300 px-3 py-3 text-left"
                                >
                                    <MapPin className={`h-5 w-5 shrink-0 ${koordinat ? 'text-blue-600' : 'text-gray-400'}`} />
                                    <span className={`min-w-0 flex-1 truncate text-sm ${koordinat ? 'text-gray-900' : 'text-gray-500'}`}>
                                        {koordinat
                                            ? `${koordinat.lat.toFixed(6)}, ${koordinat.lng.toFixed(6)}`
                                            : 'Pilih lokasi di peta'}
                                    </span>
                                    <span className="shrink-0 text-xs font-semibold text-blue-700">{koordinat ? 'Ubah' : 'Pilih'}</span>
                                </button>
                            </div>

                            <div className="grid grid-cols-1 gap-4">
                                <PhotoInput label="Foto rumah" file={fotoRumah} onOpenCamera={() => openCamera({ label: 'Foto rumah', onDone: setFotoRumah })} required />
                                <PhotoInput label="Foto segel sebelum" file={meterSebelum} onOpenCamera={() => openCamera({ label: 'Foto segel sebelum', onDone: setMeterSebelum })} required />
                                <PhotoInput label="Foto segel sesudah" file={meterSesudah} onOpenCamera={() => openCamera({ label: 'Foto segel sesudah', onDone: setMeterSesudah })} />
                                <PhotoInput label="Foto segel aktif" file={meterAktif} onOpenCamera={() => openCamera({ label: 'Foto segel aktif', onDone: setMeterAktif })} />
                            </div>

                            {error && <p role="alert" className="text-sm text-red-600">{error}</p>}

                            <button
                                type="submit"
                                disabled={saving}
                                className="w-full rounded-xl bg-blue-600 px-4 py-3 font-semibold text-white disabled:opacity-60"
                            >
                                {saving ? 'Menyimpan...' : formMode === 'history' ? 'Simpan History' : 'Simpan Segel'}
                            </button>
                        </form>
                    </section>
                </div>
            )}

            {selectedSegel && (
                <div className="absolute inset-0 z-50 bg-black/50">
                    <section className="mx-auto flex h-full max-w-lg flex-col bg-white">
                        {/* Header tetap: tidak ikut ter-scroll */}
                        <div className="shrink-0 border-b border-gray-200 px-5 pb-3 pt-5">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <h2 className="text-xl font-bold">Detail Segel</h2>
                                    <p className="mt-1 text-sm text-gray-500">Nomor Sambung : {selectedSegel.nsb}</p>
                                </div>
                                <button type="button" onClick={() => closeModal('detail')} aria-label="Tutup detail" className="shrink-0 rounded-lg p-2 text-gray-500">
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                        </div>

                        {/* Hanya area ini yang di-scroll */}
                        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
                        {loadingHistory ? (
                            <p className="py-8 text-center text-sm text-gray-500">Memuat riwayat...</p>
                        ) : history.length === 0 ? (
                            <p className="rounded-xl bg-gray-50 p-4 text-center text-sm text-gray-500">Belum ada riwayat segel.</p>
                        ) : (
                            <div className="space-y-4">
                                {[...history]
                                    .sort((a, b) => {
                                        const dateA = a.modified_at ? new Date(a.modified_at).getTime() : 0;
                                        const dateB = b.modified_at ? new Date(b.modified_at).getTime() : 0;
                                        return dateB - dateA;
                                    })
                                    .map((item, index) => {
                                    const meter = parseFotoMeter(item.foto_meter);
                                    const sebelum = meter?.meter_sebelum?.image;
                                    const sesudah = meter?.meter_sesudah?.image;
                                    const rumah = item.foto_rumah;

                                    return (
                                        <article key={`${item.modified_at ?? 'history'}-${index}`} className="rounded-xl border border-gray-200 p-4">
                                            <div className="flex items-start justify-between gap-2">
                                                <p className="min-w-0 text-sm font-semibold text-gray-800">
                                                    {item.nama || '-'} - {item.nsb || '-'}
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setDeleteError('');
                                                        openModal('delete');
                                                        setConfirmDelete({ kind: 'history', item });
                                                    }}
                                                    aria-label="Hapus history"
                                                    className="flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                    Hapus
                                                </button>
                                            </div>
                                            <p className="mt-1 text-xs text-gray-500">
                                                {item.alamat || '-'}
                                            </p>
                                            <p className="mt-1 text-xs text-gray-500">
                                                Meter lalu: {item.m_lalu || '-'}
                                            </p>
                                            <p className={`mt-1 text-xs ${item.status === 'AKTIF' ? 'text-green-500' : 'text-red-500'}`}>
                                                Status: {item.status || '-'}
                                            </p>
                                            <p className="mt-1 text-xs text-gray-500">
                                                {item.modified_at ? new Date(item.modified_at).toLocaleString('id-ID') : ''}
                                            </p>
                                                <div className="mt-3 space-y-3">
                                                    {rumah && (
                                                        <figure className="mx-auto w-3/4">
                                                            <PhotoButton path={rumah} label="Foto rumah" imgClassName="h-40 w-full rounded-lg object-cover" onOpen={openPreview} />
                                                            <figcaption className="mt-1 text-center text-xs text-gray-500">Foto rumah</figcaption>
                                                        </figure>
                                                    )}
                                                    
                                                    {
                                                        item.status === "AKTIF" ?
                                                        <figure className="mx-auto w-3/4">
                                                            <PhotoButton path={sebelum} label="Foto rumah" imgClassName="h-40 w-full rounded-lg object-cover" onOpen={openPreview} />
                                                            <figcaption className="mt-1 text-center text-xs text-gray-500">Meter Aktif</figcaption>
                                                        </figure>
                                                        :
                                                        <div className="grid grid-cols-2 gap-3">
                                                            <figure>
                                                                {sebelum ? (
                                                                    <PhotoButton path={sebelum} label="Segel sebelum" imgClassName="h-28 w-full rounded-lg object-cover" onOpen={openPreview} />
                                                                ) : (
                                                                    <div className="flex h-28 w-full items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 text-xs text-gray-400">
                                                                        Belum ada foto
                                                                    </div>
                                                                )}
                                                                <figcaption className="mt-1 text-center text-xs text-gray-500">Segel sebelum</figcaption>
                                                            </figure>
                                                            <figure>
                                                                {sesudah ? (
                                                                    <PhotoButton path={sesudah} label="Segel sesudah" imgClassName="h-28 w-full rounded-lg object-cover" onOpen={openPreview} />
                                                                ) : (
                                                                    <SesudahUploader onUpload={(file) => uploadMeterSesudah(item, file)} />
                                                                )}
                                                                <figcaption className="mt-1 text-center text-xs text-gray-500">Segel sesudah</figcaption>
                                                            </figure>
                                                        </div>
                                                    } 
                                                </div>
                                            {item.koordinat && (
                                                <a
                                                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(item.koordinat)}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="mt-3 flex justify-center text-sm text-blue-700"
                                                >
                                                    Buka lokasi di Maps
                                                </a>
                                            )}
                                        </article>
                                    );
                                    })}
                            </div>
                        )}
                        </div>

                        {/* Footer tetap: hapus segel dan tambah history baru untuk pelanggan ini */}
                        <div className="flex shrink-0 gap-3 border-t border-gray-200 bg-white px-5 py-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setDeleteError('');
                                    openModal('delete');
                                    setConfirmDelete({ kind: 'segel' });
                                }}
                                className="flex items-center justify-center gap-2 rounded-xl border border-red-300 px-4 py-3 font-semibold text-red-600 hover:bg-red-50 active:bg-red-100"
                            >
                                <Trash2 className="h-5 w-5" />
                                Hapus
                            </button>
                            <button
                                type="button"
                                onClick={openHistoryForm}
                                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 font-semibold text-white shadow-sm hover:bg-blue-700 active:bg-blue-800"
                            >
                                <Plus className="h-5 w-5" />
                                Tambah History
                            </button>
                        </div>
                    </section>
                </div>
            )}

            {showMap && (
                <LocationPicker
                    initial={koordinat}
                    onCancel={() => closeModal('map')}
                    onConfirm={(point) => {
                        setKoordinat(point);
                        closeModal('map');
                    }}
                />
            )}

            {camera && (
                <CameraCapture label={camera.label} onCapture={camera.onDone} onClose={() => closeModal('camera')} />
            )}

            {confirmDelete && (
                <div
                    role="alertdialog"
                    aria-modal="true"
                    aria-labelledby="judul-hapus"
                    className="absolute inset-0 z-[58] flex items-center justify-center bg-black/60 p-6"
                >
                    <div className="w-full max-w-xs rounded-2xl bg-white p-5 shadow-xl">
                        <h3 id="judul-hapus" className="text-lg font-bold text-gray-900">
                            {confirmDelete.kind === 'segel' ? 'Hapus data segel?' : 'Hapus history ini?'}
                        </h3>
                        <p className="mt-2 text-sm text-gray-600">
                            {confirmDelete.kind === 'segel'
                                ? `Data segel nomor sambung ${selectedSegel?.nsb ?? ''} beserta seluruh riwayatnya akan dihapus.`
                                : `Riwayat${
                                      confirmDelete.item.modified_at
                                          ? ` tanggal ${new Date(confirmDelete.item.modified_at).toLocaleString('id-ID')}`
                                          : ''
                                  } akan dihapus.`}{' '}
                            Tindakan ini tidak bisa dibatalkan.
                        </p>

                        {deleteError && <p role="alert" className="mt-3 text-sm text-red-600">{deleteError}</p>}

                        <div className="mt-4 flex gap-3">
                            <button
                                type="button"
                                onClick={closeConfirmDelete}
                                disabled={deleting}
                                className="flex-1 rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 disabled:opacity-50"
                            >
                                Batal
                            </button>
                            <button
                                type="button"
                                onClick={() => void performDelete()}
                                disabled={deleting}
                                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
                            >
                                {deleting && <LoaderCircle className="h-4 w-4 animate-spin" />}
                                {deleting ? 'Menghapus' : 'Hapus'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {previewImage && (
                <div
                    role="dialog"
                    aria-label={previewImage.label}
                    onClick={() => closeModal('preview')}
                    className="absolute inset-0 z-[60] flex flex-col bg-black/95"
                >
                    <div className="flex shrink-0 items-center justify-between px-4 py-3 text-white">
                        <p className="text-sm font-medium">{previewImage.label}</p>
                        <button
                            type="button"
                            onClick={() => closeModal('preview')}
                            aria-label="Tutup preview"
                            className="rounded-lg p-2"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                    <div className="flex min-h-0 flex-1 items-center justify-center p-4">
                        <img
                            src={previewImage.src}
                            alt={previewImage.label}
                            className="max-h-full max-w-full rounded-lg object-contain"
                        />
                    </div>
                </div>
            )}
        </main>
    );
}